import {Component, DoCheck, EventEmitter, inject, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {FormControl} from '@angular/forms';
import {Subscription} from 'rxjs';
import {
  ImportModuleConceptCandidate,
  ImportModuleConceptField,
  ImportModuleConceptRow,
} from '../../../../core/definitions/import-module';
import {PrimusRouterService} from '../../../../core/primus-router.service';
import {ConceptOption} from '../../concept-picker/concept-option.model';
import {
  addCandidate,
  mostUsedCandidate,
  pickedCandidate,
  PieceView,
  selectCandidate,
  shortTermId,
} from '../value-review.model';

/**
 * The expanded part of one value-review row, for its decision: the
 * candidates to pick from plus a search over the whole list (`Velg`), the
 * new term's name, code and parent (`Opprett`), or each piece and what
 * the run does with it (`Del opp`).
 *
 * Edits `row` in place and emits `changed` after each edit; the page owns
 * the rows and recomputes what depends on them.
 */
@Component({
  selector: 'app-value-review-row-detail',
  templateUrl: './value-review-row-detail.component.html',
  styleUrls: ['./value-review-row-detail.component.scss'],
  standalone: false,
})
export class ValueReviewRowDetailComponent implements OnInit, DoCheck, OnDestroy {
  @Input({required: true}) row!: ImportModuleConceptRow;
  @Input({required: true}) field!: ImportModuleConceptField;
  @Input({required: true}) fieldLabel!: string;
  @Input({required: true}) objectType!: string;
  /** The field's terms a new term can go under; empty for a flat list. */
  @Input() parentOptions: ConceptOption[] = [];
  /** A composite row's pieces and their outcomes. */
  @Input() pieceViews: PieceView[] = [];

  @Output() changed = new EventEmitter<void>();
  @Output() jumpToRow = new EventEmitter<ImportModuleConceptRow>();

  readonly parentControl = new FormControl<string | null>(null);
  shortId = (candidate: ImportModuleConceptCandidate): string => shortTermId(candidate.candidate_artifact_id);

  private readonly primusRouter: PrimusRouterService = inject(PrimusRouterService);
  private readonly subscriptions = new Subscription();
  private readonly termUrls = new Map<string, string>();

  ngOnInit(): void {
    this.parentControl.setValue(this.row.new_concept_parent_id ?? null, {emitEvent: false});
    this.subscriptions.add(this.parentControl.valueChanges.subscribe(parentId => {
      this.row.new_concept_parent_id = parentId;
      this.changed.emit();
    }));
  }

  /** A bulk action on the page can set the parent of this row while it is
   * open. */
  ngDoCheck(): void {
    const parentId = this.row.new_concept_parent_id ?? null;
    if (this.parentControl.value !== parentId) {
      this.parentControl.setValue(parentId, {emitEvent: false});
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /** The term's own page, as the concept lists open it. */
  termUrl(candidate: ImportModuleConceptCandidate): string {
    const id = candidate.candidate_artifact_id;
    let url = this.termUrls.get(id);
    if (url === undefined) {
      url = this.primusRouter.stateUrl('home.primus.artifact',
        {artifactId: id, listName: 'overview', admin_type: 'concepts'});
      this.termUrls.set(id, url);
    }
    return url;
  }

  get pickedId(): string | null {
    return pickedCandidate(this.row)?.candidate_artifact_id ?? null;
  }

  get mostUsed(): ImportModuleConceptCandidate | null {
    return mostUsedCandidate(this.row);
  }

  pick(candidateId: string): void {
    selectCandidate(this.row, candidateId);
    this.changed.emit();
  }

  /** A term found by searching the whole list: added to the candidates if
   * the row does not carry it, and picked. */
  onTermPicked(option: ConceptOption): void {
    addCandidate(this.row, option);
    this.pick(option.id);
  }

  onNewTermEdited(): void {
    this.changed.emit();
  }
}
